import { userAtom } from '@/atoms/user.atom';
import { batchStatusMap, formatDateTime } from '@/components/BatchList';
import { Button } from '@/components/ui/button';
import { getPlaceholderAvatar } from '@/core/avatar';
import { execute } from '@/graphql/execute';
import { Project } from '@/graphql/graphql';
import { usePageMeta } from '@/hooks/usePageMeta';
import PageSkeleton from '@/layout/Skeleton';
import { useAlert } from '@/providers/AlertProvider';
import { deleteBatchMutation, getBatch, resetBatchMutation } from '@/queries/attachment.query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { RotateCcw, Trash2 } from 'lucide-react';
import { analytics } from '@/lib/analytics';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext, useParams } from 'react-router';
import { toast } from 'sonner';
import AttachmentList from './AttachmentList';
import Header from './Header';
import JobList from './JobList';

interface iindexBatchPage {}

const BatchPage: FunctionComponent<iindexBatchPage> = () => {
  const { t } = useTranslation('project');
  const [shouldPoll, setShouldPoll] = useState(false);
  const user = useAtomValue(userAtom);
  const isActegonUser = user?.email?.endsWith('@actegon.com') ?? false;
  const { href, project, projectLoading } = useOutletContext<{
    href: string;
    project: Project;
    projectLoading: boolean;
  }>();
  const { batchId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { openAlert } = useAlert();

  const deleteBatch = useMutation({
    mutationFn: () => execute(deleteBatchMutation, { id: batchId! }),
    onSuccess: () => {
      analytics.capture('Batch Deleted');
      queryClient.invalidateQueries({ queryKey: ['batches'] });
      toast.success(t('batch.batchDeleted'));
      navigate(`${href}/upload`);
    },
    onError: (error: any) => {
      toast.error(error.message || t('batch.failedDeleteBatch'));
    },
  });

  const resetBatch = useMutation({
    mutationFn: () => execute(resetBatchMutation, { id: batchId! }),
    onSuccess: () => {
      analytics.capture('Batch Reset');
      queryClient.invalidateQueries({ queryKey: ['batch', batchId] });
      queryClient.invalidateQueries({ queryKey: ['batches'] });
      toast.success(t('batch.batchReset'));
    },
    onError: (error: any) => {
      toast.error(error.message || t('batch.failedResetBatch'));
    },
  });

  const handleDelete = () => {
    openAlert({
      title: t('batch.deleteBatchTitle'),
      content: t('batch.deleteBatchDescription'),
      actions: [{ label: t('batch.delete'), variant: 'destructive', onClick: () => deleteBatch.mutate() }],
    });
  };

  const handleReset = () => {
    openAlert({
      title: t('batch.resetBatchTitle'),
      content: t('batch.resetBatchDescription'),
      actions: [{ label: t('batch.reset'), variant: 'destructive', onClick: () => resetBatch.mutate() }],
    });
  };

  const { data: batchData, isLoading } = useQuery({
    queryKey: ['batch', batchId],
    queryFn: () => execute(getBatch, { id: batchId! }),
    refetchInterval: shouldPoll ? 500 : false,
  });

  const batch = batchData?.batch;
  usePageMeta({ title: [batch?.key ? t('batch.batchKey', { key: batch.key.toUpperCase() }) : undefined, project?.name] });

  useEffect(() => {
    setShouldPoll(batch?.status === 'PROCESSING' || batch?.status === 'UPLOADING');
  }, [batch?.status]);

  if (!batch) return null;
  return (
    <PageSkeleton
      steps={[
        { label: projectLoading ? t('edit.loading') : project.name, href },
        { label: t('batch.uploadFiles'), href: `${href}/upload` },
        {
          label: isLoading ? t('edit.loading') : t('batch.batchKey', { key: batch?.key?.toUpperCase() }),
          href: `${href}/batch/${batchId}`,
        },
      ]}>
      <div className='max-w-screen-md px-4 mx-auto w-full pb-12 pt-12'>
        <Header status={batch?.status} srid={batch.srid || project.srid} attachments={batch?.attachments ?? []} />
        <div className='border rounded-md border-slate-200/70 mt-0 bg-white dark:bg-stone-900 dark:border-stone-800 px-4 py-2.5 items-start gap-4 grid grid-cols-4'>
          <div className='flex items-start gap-0 flex-col'>
            <span className='text-sm text-slate-500 dark:text-stone-500 font-medium text-left'>
              {t('batch.status')}
            </span>
            <span className='font-medium text-slate-900 dark:text-white text-left flex items-center gap-1'>
              {batchStatusMap[batch?.status].icon}
              {batchStatusMap[batch?.status].label}
            </span>
          </div>
          <div className='flex items-start gap-0 flex-col'>
            <span className='text-sm text-slate-500 dark:text-stone-500 font-medium text-left'>{t('batch.label')}</span>
            <span className='font-medium text-slate-900 dark:text-white text-left'>
              {t('batch.batchKey', { key: batch?.key?.toUpperCase() })}
            </span>
          </div>
          <div className='flex items-start gap-0 flex-col'>
            <span className='text-sm text-slate-500 dark:text-stone-500 font-medium text-left'>
              {t('batch.uploadedBy')}
            </span>
            <span className='font-medium text-slate-900 dark:text-white text-left flex items-center gap-1.5'>
              <img
                src={
                  batch?.createdBy?.avatar?.url ||
                  getPlaceholderAvatar(batch?.createdBy?.email || batch?.createdBy?.id || '')
                }
                alt={batch?.createdBy?.name || t('batch.unknown')}
                className='size-4 rounded-full'
              />
              {batch?.createdBy?.name}
            </span>
          </div>
          <div className='flex items-start gap-0 flex-col'>
            <span className='text-sm text-slate-500 dark:text-stone-500 font-medium text-left'>
              {t('batch.uploadedAt')}
            </span>
            <span className='font-medium text-slate-900 dark:text-white text-left'>
              {formatDateTime(batch?.createdAt ?? new Date())}
            </span>
          </div>
        </div>
        <AttachmentList attachments={batch?.attachments ?? []} batchStatus={batch?.status} />
        <JobList jobs={batch?.jobs ?? []} />
        <div className='mt-6 flex justify-end gap-2'>
          {isActegonUser && (
            <Button variant='outline' onClick={handleReset} disabled={resetBatch.isPending}>
              <RotateCcw className='size-4 mr-2' />
              {t('batch.resetBatch')}
            </Button>
          )}
          <Button variant='destructive' onClick={handleDelete} disabled={deleteBatch.isPending}>
            <Trash2 className='size-4 mr-2' />
            {t('batch.deleteBatch')}
          </Button>
        </div>
      </div>
    </PageSkeleton>
  );
};

export default BatchPage;
